import { useCallback, useEffect, useRef, useState } from 'react';

type ConnectionStatus = 'disconnected' | 'connecting' | 'live' | 'muted' | 'error';

interface SquawkPlayerProps {
  label: string;
  streamUrl: string;
  sdkUrl: string;
  adapterUrl: string;
  /** Async callback to get an authenticated stream URL. When provided, called on each play. */
  getStreamUrl?: () => Promise<string>;
}

/** Map 0–100 slider to exponential gain (0 dB at 100, -60 dB at 1, silent at 0). */
function sliderToGain(value: number): number {
  const v = Math.max(0, Math.min(100, value));
  if (v === 0) return 0;
  const minDb = -60;
  const t = (v - 1) / 99; // 1..100 → 0..1
  const db = minDb * (1 - t); // -60..0
  return Math.pow(10, db / 20);
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window { SrsRtcPlayerAsync: any }
}

/**
 * Squawk audio player for the SRS WebRTC stream.
 * Renders inline within the AudioDrawer.
 */
export function SquawkPlayer({ label, streamUrl, sdkUrl, adapterUrl, getStreamUrl }: SquawkPlayerProps) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [volume, setVolume] = useState(100);
  const [sdkReady, setSdkReady] = useState(false);

  const sdkRef = useRef<ReturnType<typeof window.SrsRtcPlayerAsync> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const wasPlayingRef = useRef(false);

  // Load SRS SDK scripts once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadScript(adapterUrl);
        await loadScript(sdkUrl);
        if (!cancelled) setSdkReady(true);
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [adapterUrl, sdkUrl]);

  const teardown = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch { /* noop */ }
      sourceRef.current = null;
    }
    if (sdkRef.current) {
      try { sdkRef.current.close(); } catch { /* noop */ }
      sdkRef.current = null;
    }
    if (audioElRef.current) {
      audioElRef.current.srcObject = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      teardown();
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, [teardown]);

  const start = useCallback(async () => {
    if (!sdkReady) return;
    setStatus('connecting');

    teardown();

    const sdk = new window.SrsRtcPlayerAsync();
    sdkRef.current = sdk;

    // Force audio-only negotiation. The SRS SDK player adds a recvonly *video*
    // transceiver by default, so SRS delivers the publisher's (unwatched) video
    // bundled with the Opus audio on a single transport. Large video keyframe
    // bursts overflow listeners' send buffers and drop the bundled audio →
    // choppy squawk (observed on the 1080p zeroday feed, 2026-06). Suppressing
    // the video m-line makes the offer audio-only, so SRS sends audio alone.
    const pc = sdk.pc as RTCPeerConnection | undefined;
    if (pc) {
      const addTransceiver = pc.addTransceiver.bind(pc);
      pc.addTransceiver = ((kind: string, init?: RTCRtpTransceiverInit) =>
        kind === 'video' ? undefined : addTransceiver(kind, init)) as RTCPeerConnection['addTransceiver'];
    }

    // Hidden audio element (muted — Web Audio handles output)
    if (!audioElRef.current) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.style.display = 'none';
      el.volume = 0;
      document.body.appendChild(el);
      audioElRef.current = el;
    }
    audioElRef.current.srcObject = sdk.stream;
    audioElRef.current.volume = 0;

    // Create AudioContext + gain on first play
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
      gainRef.current = audioCtxRef.current.createGain();
      gainRef.current.connect(audioCtxRef.current.destination);
    }

    // Resume suspended context (autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }

    gainRef.current!.gain.value = sliderToGain(volume);

    try {
      const url = getStreamUrl ? await getStreamUrl() : streamUrl;
      await sdk.play(url);

      sourceRef.current = audioCtxRef.current!.createMediaStreamSource(sdk.stream);
      if (volume > 0) {
        sourceRef.current.connect(gainRef.current!);
      }

      wasPlayingRef.current = true;
      setStatus('live');
    } catch {
      setStatus('error');
    }
  }, [sdkReady, volume, teardown, streamUrl, getStreamUrl]);

  const stop = useCallback(() => {
    wasPlayingRef.current = false;
    teardown();
    setStatus('disconnected');
  }, [teardown]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);

    if (newVolume === 0) {
      if (sdkRef.current) {
        wasPlayingRef.current = true;
        teardown();
        setStatus('muted');
      } else {
        setStatus((prev) => (prev === 'disconnected' ? prev : 'muted'));
      }
      return;
    }

    // Apply gain if connected
    if (gainRef.current) {
      gainRef.current.gain.value = sliderToGain(newVolume);
    }

    // Auto-reconnect if was muted while playing
    if (!sdkRef.current && wasPlayingRef.current) {
      // Will trigger start via effect-like flow
      void start();
    }
  }, [teardown, start]);

  const statusColor = {
    disconnected: 'bg-white/35',
    connecting: 'bg-accent-yellow animate-pulse',
    live: 'bg-bull-blue',
    muted: 'bg-white/35',
    error: 'bg-bear-red',
  }[status];

  const statusText = {
    disconnected: 'Disconnected',
    connecting: 'Connecting...',
    live: 'Live',
    muted: 'Muted',
    error: 'Error',
  }[status];

  const isPlaying = status === 'live' || status === 'connecting';

  return (
    <div className="px-3 py-3.5 border-b border-dark-border">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-xs font-semibold text-white tracking-wide">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
          <span className="text-[10px] text-gray-400">{statusText}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        {!isPlaying ? (
          <button
            onClick={start}
            disabled={!sdkReady}
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-dark-surface-light border border-dark-border text-white hover:border-blue-400/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Play
          </button>
        ) : (
          <button
            onClick={stop}
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-dark-surface-light border border-dark-border text-gray-300 hover:border-bear-red/50 transition-colors"
          >
            Stop
          </button>
        )}

        {/* Volume slider */}
        <input
          type="range"
          min={0}
          max={100}
          value={volume}
          onChange={(e) => handleVolumeChange(Number(e.target.value))}
          className="squawk-volume flex-1 h-1 accent-blue-400 bg-transparent"
        />
        <span className="text-[10px] text-gray-500 w-7 text-right tabular-nums">
          {volume}%
        </span>
      </div>
    </div>
  );
}
